import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { staffService } from '../../../services/staffService';
import Icon from '../../../components/AppIcon';

const StaffLoginForm = ({ onSwitchToRegular }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target || {};
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const { user, staffMember, error } = await staffService?.verifyStaffLogin(
        formData?.email,
        formData?.password
      );

      if (error) {
        setErrors({ general: error });
        return;
      }

      // Redirect based on staff role or assignment
      const from = location?.state?.from?.pathname || '/attendee-management';
      navigate(from, { replace: true });
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card border border-border rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mx-auto mb-4">
            <Icon name="UserCheck" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            Staff Login
          </h1>
          <p className="text-muted-foreground">
            Sign in with your staff credentials
          </p>
        </div>

        {/* Error Message */}
        {errors?.general && (
          <div className="mb-6 p-4 rounded-lg border bg-error/10 border-error/20 text-error">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm">{errors?.general}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="your-email@example.com"
            value={formData?.email || ''}
            onChange={handleInputChange}
            error={errors?.email}
            required
          />

          <Input
            label="Password"
            type="password"
            name="password"
            placeholder="Enter your password"
            value={formData?.password || ''}
            onChange={handleInputChange}
            error={errors?.password}
            required
          />

          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isLoading}
            iconName="LogIn"
            iconPosition="left"
            className="bg-green-600 hover:bg-green-700"
          >
            Sign In as Staff
          </Button>
        </form>

        {/* Switch to Regular Login */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground">
            Not a staff member?
            <button
              type="button"
              onClick={onSwitchToRegular}
              className="ml-2 text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Regular Login
            </button>
          </p>
        </div>

        {/* Staff Info */}
        <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border">
          <h3 className="text-sm font-medium text-foreground mb-2">For Staff Members:</h3>
          <div className="space-y-1 text-xs text-muted-foreground">
            <p>• Use the email and password provided by your administrator</p>
            <p>• Contact your event host if you need password assistance</p>
            <p>• Your access is limited to assigned events and check-in functions</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffLoginForm;